/**
 * Multilingual Input Service
 * Implements a Deterministic Finite State Machine (DFSM) for real-time
 * keyboard layout mapping. Supports Indic (InScript), Arabic, French, and Spanish.
 */

export type LanguageType =
  | "English"
  // | "Hindi"
  // | "Gujarati"
  // | "Tamil"
  // | "Telugu"
  // | "Malayalam"
  // | "Kannada"
  // | "Punjabi"
  // | "Bengali"
  // | "Marathi"
  | "Hindi Phonetic"
  | "Gujarati Phonetic";
// | "Arabic"
// | "French"
// | "Spanish";

export interface LanguageConfig {
  name: string;
  dir: "ltr" | "rtl";
  font: string;
  mapping: Record<string, { default: string; shift: string; altGr?: string }>;
  isPhonetic?: boolean;
}

const INSCRIPT_BASE_LAYOUT = {
  // Row 1 (Digits)
  Digit1: { default: "1", shift: "!" },
  Digit2: { default: "2", shift: "@" },
  Digit3: { default: "3", shift: "#" },
  Digit4: { default: "4", shift: "$" },
  Digit5: { default: "5", shift: "%" },
  Digit6: { default: "6", shift: "^" },
  Digit7: { default: "7", shift: "&" },
  Digit8: { default: "8", shift: "*" },
  Digit9: { default: "9", shift: "(" },
  Digit0: { default: "0", shift: ")" },

  // Row 2
  KeyQ: { default: "ૌ", shift: "ઔ" },
  KeyW: { default: "ૈ", shift: "ઐ" },
  KeyE: { default: "ા", shift: "આ" },
  KeyR: { default: "ી", shift: "ઈ" },
  KeyT: { default: "ૂ", shift: "ઊ" },
  KeyY: { default: "બ", shift: "ભ" },
  KeyU: { default: "હ", shift: "ઙ" },
  KeyI: { default: "ગ", shift: "ઘ" },
  KeyO: { default: "દ", shift: "ધ" },
  KeyP: { default: "જ", shift: "झ" },
  BracketLeft: { default: "ડ", shift: "ઢ" },
  BracketRight: { default: "ૃ", shift: "ઞ" },

  // Row 3
  KeyA: { default: "ો", shift: "ઓ" },
  KeyS: { default: "ે", shift: "એ" },
  KeyD: { default: "્", shift: "અ" },
  KeyF: { default: "િ", shift: "ઇ" },
  KeyG: { default: "ુ", shift: "ઉ" },
  KeyH: { default: "પ", shift: "ફ" },
  KeyJ: { default: "ર", shift: "ऱ" },
  KeyK: { default: "ક", shift: "ખ" },
  KeyL: { default: "ત", shift: "थ" },
  Semicolon: { default: "ચ", shift: "છ" },
  Quote: { default: "ટ", shift: "ઠ" },
  Backslash: { default: "ૉ", shift: "ऑ" },

  // Row 4
  KeyZ: { default: "़", shift: "ा" },
  KeyX: { default: "ं", shift: "ँ" },
  KeyC: { default: "મ", shift: "ણ" },
  KeyV: { default: "ન", shift: "ऩ" },
  KeyB: { default: "વ", shift: "વ" },
  KeyN: { default: "લ", shift: "ળ" },
  KeyM: { default: "સ", shift: "શ" },
  Comma: { default: "ય", shift: "ય" },
  Period: { default: "।", shift: "ષ" },
  Slash: { default: "य", shift: "य" },
};

const HINDI_MAPPING = {
  ...INSCRIPT_BASE_LAYOUT,
  Digit1: { default: "१", shift: "ऍ" },
  Digit2: { default: "२", shift: "ॅ" },
  Digit3: { default: "३", shift: "્ર" },
  Digit4: { default: "४", shift: "ર્" },
  Digit5: { default: "५", shift: "ज्ञ" },
  Digit6: { default: "६", shift: "त्र" },
  Digit7: { default: "७", shift: "क्ष" },
  Digit8: { default: "८", shift: "श्र" },
  Digit9: { default: "९", shift: "(" },
  Digit0: { default: "०", shift: ")" },
  Minus: { default: "-", shift: "ः" },
  Equal: { default: "ृ", shift: "ऋ" },
  KeyZ: { default: "़", shift: "ॅ" },
  Backslash: { default: "ॉ", shift: "ऑ" },
  Slash: { default: "य", shift: "य" },
  Period: { default: "।", shift: "ष" },
};

const GUJARATI_MAPPING = {
  Digit1: { default: "૧", shift: "ૅ" },
  Digit2: { default: "૨", shift: "ઁ" },
  Digit3: { default: "૩", shift: "્ર" },
  Digit4: { default: "૪", shift: "ર્" },
  Digit5: { default: "૫", shift: "જ્ઞ" },
  Digit6: { default: "૬", shift: "ત્ર" },
  Digit7: { default: "૭", shift: "ક્ષ" },
  Digit8: { default: "૮", shift: "શ્ર" },
  Digit9: { default: "૯", shift: "(" },
  Digit0: { default: "૦", shift: ")" },
  Minus: { default: "ઙ", shift: "ઃ" },
  Equal: { default: "ૃ", shift: "ઋ" },
  KeyQ: { default: "ૌ", shift: "ઔ" },
  KeyW: { default: "ૈ", shift: "ઐ" },
  KeyE: { default: "ા", shift: "આ" },
  KeyR: { default: "ી", shift: "ઈ" },
  KeyT: { default: "ૂ", shift: "ઊ" },
  KeyY: { default: "બ", shift: "ભ" },
  KeyU: { default: "હ", shift: "ઙ" },
  KeyI: { default: "ગ", shift: "ઘ" },
  KeyO: { default: "દ", shift: "ધ" },
  KeyP: { default: "જ", shift: "ઝ" },
  BracketLeft: { default: "ડ", shift: "ઢ" },
  BracketRight: { default: "ૃ", shift: "ઞ" },
  Backslash: { default: "ૉ", shift: "ઑ" },
  KeyA: { default: "ો", shift: "ઓ" },
  KeyS: { default: "ે", shift: "એ" },
  KeyD: { default: "્", shift: "અ" },
  KeyF: { default: "િ", shift: "ઇ" },
  KeyG: { default: "ુ", shift: "ઉ" },
  KeyH: { default: "પ", shift: "ફ" },
  KeyJ: { default: "ર", shift: "ર" },
  KeyK: { default: "ક", shift: "ખ" },
  KeyL: { default: "ત", shift: "થ" },
  Semicolon: { default: "ચ", shift: "છ" },
  Quote: { default: "ટ", shift: "ઠ" },
  KeyZ: { default: "ૅ", shift: "ઍ" },
  KeyX: { default: "ં", shift: "ઁ" },
  KeyC: { default: "મ", shift: "ણ" },
  KeyV: { default: "ન", shift: "ન" },
  KeyB: { default: "વ", shift: "વ" },
  KeyN: { default: "લ", shift: "ળ" },
  KeyM: { default: "સ", shift: "શ" },
  Comma: { default: "ય", shift: "ય" },
  Period: { default: ".", shift: "ષ" },
  Slash: { default: "ય", shift: "ય" },
};

const TAMIL_MAPPING = {
  KeyQ: { default: "ௌ", shift: "ஔ" },
  KeyW: { default: "ை", shift: "ஐ" },
  KeyE: { default: "ா", shift: "ஆ" },
  KeyR: { default: "ீ", shift: "ஈ" },
  KeyT: { default: "ூ", shift: "ஊ" },
  KeyY: { default: "ப", shift: "ப" },
  KeyU: { default: "ஹ", shift: "ஹ" },
  KeyI: { default: "க", shift: "க" },
  KeyO: { default: "த", shift: "த" },
  KeyP: { default: "ஜ", shift: "ஜ" },
  KeyA: { default: "ொ", shift: "ஓ" },
  KeyS: { default: "ெ", shift: "ஏ" },
  KeyD: { default: "்", shift: "அ" },
  KeyF: { default: "ி", shift: "இ" },
  KeyG: { default: "ு", shift: "உ" },
  KeyK: { default: "க", shift: "க" },
  KeyL: { default: "த", shift: "த" },
};

const ARABIC_MAPPING = {
  KeyQ: { default: "ض", shift: "َ" },
  KeyW: { default: "ص", shift: "ً" },
  KeyE: { default: "ث", shift: "ُ" },
  KeyR: { default: "ق", shift: "ٌ" },
  KeyT: { default: "ف", shift: "لإ" },
  KeyY: { default: "غ", shift: "إ" },
  KeyU: { default: "ع", shift: "`" },
  KeyI: { default: "ه", shift: "÷" },
  KeyO: { default: "خ", shift: "×" },
  KeyP: { default: "ح", shift: "؛" },
  KeyA: { default: "ش", shift: "ِ" },
  KeyS: { default: "س", shift: "ٍ" },
  KeyD: { default: "ي", shift: "[" },
  KeyF: { default: "ب", shift: "]" },
  KeyG: { default: "ل", shift: "لأ" },
  KeyH: { default: "ا", shift: "أ" },
  KeyJ: { default: "ت", shift: "ـ" },
  KeyK: { default: "ن", shift: "،" },
  KeyL: { default: "م", shift: "/" },
  KeyZ: { default: "ئ", shift: "~" },
  KeyX: { default: "ء", shift: "ْ" },
  KeyC: { default: "ؤ", shift: "{" },
  KeyV: { default: "ر", shift: "}" },
  KeyB: { default: "ला", shift: "لآ" },
  KeyN: { default: "ى", shift: "آ" },
  KeyM: { default: "ة", shift: "’" },
};

const HINDI_PHONETIC_MAPPING = {
  KeyA: { default: "ा", shift: "अ" },
  KeyB: { default: "ब", shift: "भ" },
  KeyC: { default: "च", shift: "छ" },
  KeyD: { default: "द", shift: "ड" }, // d -> dental, D -> retroflex
  KeyE: { default: "े", shift: "ए" },
  KeyF: { default: "फ", shift: "फ" },
  KeyG: { default: "ग", shift: "घ" },
  KeyH: { default: "ह", shift: "ः" },
  KeyI: { default: "ि", shift: "इ" },
  KeyJ: { default: "ज", shift: "झ" },
  KeyK: { default: "क", shift: "ख" },
  KeyL: { default: "ल", shift: "ळ" }, // l -> dental, L -> retroflex
  KeyM: { default: "म", shift: "ं" },
  KeyN: { default: "न", shift: "ण" }, // n -> dental, N -> retroflex
  KeyO: { default: "ो", shift: "ओ" },
  KeyP: { default: "प", shift: "फ" },
  KeyQ: { default: "્", shift: "ऋ" }, // q -> halant (virama)
  KeyR: { default: "र", shift: "र्" },
  KeyS: { default: "स", shift: "ष" }, // s -> dental, S -> retroflex
  KeyT: { default: "त", shift: "ट" }, // t -> dental, T -> retroflex
  KeyU: { default: "ु", shift: "उ" },
  KeyV: { default: "व", shift: "व" },
  KeyW: { default: "व", shift: "व" },
  KeyX: { default: "ક્ષ", shift: "જ્ઞ" },
  KeyY: { default: "य", shift: "य" },
  KeyZ: { default: "ज", shift: "झ" },
  Digit1: { default: "१", shift: "!" },
  Digit2: { default: "२", shift: "@" },
  Digit3: { default: "३", shift: "#" },
  Digit4: { default: "४", shift: "$" },
  Digit5: { default: "५", shift: "%" },
  Digit6: { default: "६", shift: "^" },
  Digit7: { default: "૭", shift: "&" },
  Digit8: { default: "૮", shift: "*" },
  Digit9: { default: "૯", shift: "(" },
  Digit0: { default: "૦", shift: ")" },
};

const GUJARATI_PHONETIC_MAPPING = {
  KeyA: { default: "ા", shift: "અ" },
  KeyB: { default: "બ", shift: "ભ" },
  KeyC: { default: "ચ", shift: "છ" },
  KeyD: { default: "દ", shift: "ડ" }, // d -> dental, D -> retroflex
  KeyE: { default: "ે", shift: "એ" },
  KeyF: { default: "ફ", shift: "ફ" },
  KeyG: { default: "ગ", shift: "ઘ" },
  KeyH: { default: "હ", shift: "ઃ" },
  KeyI: { default: "િ", shift: "ઇ" },
  KeyJ: { default: "જ", shift: "ઝ" },
  KeyK: { default: "ક", shift: "ખ" },
  KeyL: { default: "લ", shift: "ળ" }, // l -> dental, L -> retroflex
  KeyM: { default: "મ", shift: "ં" },
  KeyN: { default: "ન", shift: "ણ" }, // n -> dental, N -> retroflex
  KeyO: { default: "ો", shift: "ઓ" },
  KeyP: { default: "પ", shift: "ફ" },
  KeyQ: { default: "્", shift: "ઋ" }, // q -> halant (virama)
  KeyR: { default: "ર", shift: "ર્" },
  KeyS: { default: "સ", shift: "ષ" }, // s -> dental, S -> retroflex
  KeyT: { default: "ત", shift: "ટ" }, // t -> dental, T -> retroflex
  KeyU: { default: "ુ", shift: "ઉ" },
  KeyV: { default: "વ", shift: "વ" },
  KeyW: { default: "વ", shift: "વ" },
  KeyX: { default: "ક્ષ", shift: "જ્ઞ" },
  KeyY: { default: "ય", shift: "ય" },
  KeyZ: { default: "ઝ", shift: "ષ" },
  Digit1: { default: "૧", shift: "!" },
  Digit2: { default: "૨", shift: "@" },
  Digit3: { default: "૩", shift: "#" },
  Digit4: { default: "૪", shift: "$" },
  Digit5: { default: "૫", shift: "%" },
  Digit6: { default: "૬", shift: "^" },
  Digit7: { default: "૭", shift: "&" },
  Digit8: { default: "૮", shift: "*" },
  Digit9: { default: "૯", shift: "(" },
  Digit0: { default: "૦", shift: ")" },
  BracketLeft: { default: "[", shift: "{" },
  BracketRight: { default: "]", shift: "}" },
  Quote: { default: "'", shift: '"' },
  Semicolon: { default: ";", shift: ":" },
  Comma: { default: ",", shift: "<" },
  Period: { default: ".", shift: ">" },
  Slash: { default: "/", shift: "?" },
};

export const LANGUAGE_CONFIGS: Record<LanguageType, LanguageConfig> = {
  English: {
    name: "English",
    dir: "ltr",
    font: "'Courier Prime', 'Courier', monospace",
    mapping: {},
  },
  /*
  Hindi: {
    name: "Hindi",
    dir: "ltr",
    font: "'Noto Sans Devanagari', sans-serif",
    mapping: HINDI_MAPPING,
  },
  Gujarati: {
    name: "Gujarati",
    dir: "ltr",
    font: "'Noto Sans Gujarati', sans-serif",
    mapping: GUJARATI_MAPPING,
  },
  */
  "Hindi Phonetic": {
    name: "Hindi (Phonetic)",
    dir: "ltr",
    font: "'Noto Sans Devanagari', sans-serif",
    mapping: HINDI_PHONETIC_MAPPING,
    isPhonetic: true,
  },
  "Gujarati Phonetic": {
    name: "Gujarati (Phonetic)",
    dir: "ltr",
    font: "'Noto Sans Gujarati', sans-serif",
    mapping: GUJARATI_PHONETIC_MAPPING,
    isPhonetic: true,
  },
  /*
  Tamil: {
    name: "Tamil",
    dir: "ltr",
    font: "'Noto Sans Tamil', sans-serif",
    mapping: TAMIL_MAPPING,
  },
  Arabic: {
    name: "Arabic",
    dir: "rtl",
    font: "'Amiri', serif",
    mapping: ARABIC_MAPPING,
  },
  French: {
    name: "French (AZERTY)",
    dir: "ltr",
    font: "'Courier Prime', serif",
    mapping: {
      KeyQ: { default: "a", shift: "A" },
      KeyW: { default: "z", shift: "Z" },
      KeyA: { default: "q", shift: "Q" },
      KeyM: { default: ",", shift: "?" },
      Semicolon: { default: "m", shift: "M" },
      Digit1: { default: "&", shift: "1" },
      Digit2: { default: "é", shift: "2" },
      Digit3: { default: "\"", shift: "3" },
      Digit4: { default: "'", shift: "4" },
      Digit5: { default: "(", shift: "5" },
      Digit6: { default: "-", shift: "6" },
      Digit7: { default: "è", shift: "7" },
      Digit8: { default: "_", shift: "8" },
      Digit9: { default: "ç", shift: "9" },
      Digit0: { default: "à", shift: "0" },
    },
  },
  Spanish: {
    name: "Spanish",
    dir: "ltr",
    font: "'Courier Prime', serif",
    mapping: {
      Semicolon: { default: "ñ", shift: "Ñ" },
      Quote: { default: "´", shift: "¨" },
      BracketLeft: { default: "`", shift: "^" },
    },
  },
  Telugu: {
    name: "Telugu",
    dir: "ltr",
    font: "'Noto Sans Telugu', sans-serif",
    mapping: { ...INSCRIPT_BASE_LAYOUT },
  },
  Malayalam: {
    name: "Malayalam",
    dir: "ltr",
    font: "'Noto Sans Malayalam', sans-serif",
    mapping: { ...INSCRIPT_BASE_LAYOUT },
  },
  Kannada: {
    name: "Kannada",
    dir: "ltr",
    font: "'Noto Sans Kannada', sans-serif",
    mapping: { ...INSCRIPT_BASE_LAYOUT },
  },
  Punjabi: {
    name: "Punjabi",
    dir: "ltr",
    font: "'Noto Sans Gurmukhi', sans-serif",
    mapping: { ...INSCRIPT_BASE_LAYOUT },
  },
  Bengali: {
    name: "Bengali",
    dir: "ltr",
    font: "'Noto Sans Bengali', sans-serif",
    mapping: { ...INSCRIPT_BASE_LAYOUT },
  },
  Marathi: {
    name: "Marathi",
    dir: "ltr",
    font: "'Noto Sans Devanagari', sans-serif",
    mapping: HINDI_MAPPING,
  },
  */
};

export const VOWEL_FORMS: Record<
  string,
  Record<string, { full: string; matra: string }>
> = {
  Hindi: {
    a: { full: "अ", matra: "" },
    aa: { full: "आ", matra: "ा" },
    A: { full: "आ", matra: "ा" },
    i: { full: "इ", matra: "ि" },
    I: { full: "ई", matra: "ी" },
    ee: { full: "ई", matra: "ी" },
    u: { full: "उ", matra: "ु" },
    U: { full: "ऊ", matra: "ू" },
    oo: { full: "ऊ", matra: "ू" },
    e: { full: "ए", matra: "े" },
    ai: { full: "ऐ", matra: "ै" },
    E: { full: "ऐ", matra: "ै" },
    o: { full: "ओ", matra: "ो" },
    au: { full: "औ", matra: "ौ" },
    O: { full: "औ", matra: "ौ" },
    Ru: { full: "ऋ", matra: "ृ" },
    ri: { full: "ऋ", matra: "ृ" },
  },
  Gujarati: {
    a: { full: "અ", matra: "" },
    aa: { full: "આ", matra: "ા" },
    A: { full: "આ", matra: "ા" },
    i: { full: "ઇ", matra: "િ" },
    I: { full: "ઈ", matra: "ી" },
    ee: { full: "ઈ", matra: "ી" },
    u: { full: "ઉ", matra: "ુ" },
    U: { full: "ઊ", matra: "ૂ" },
    oo: { full: "ઊ", matra: "ૂ" },
    e: { full: "એ", matra: "ે" },
    ai: { full: "ઐ", matra: "ૈ" },
    E: { full: "ઐ", matra: "ૈ" },
    o: { full: "ઓ", matra: "ો" },
    au: { full: "ઔ", matra: "ૌ" },
    O: { full: "ઔ", matra: "ૌ" },
    Ru: { full: "ઋ", matra: "ૃ" },
    ri: { full: "ઋ", matra: "ૃ" },
    "&": { full: "ઍ", matra: "ૅ" },
    $: { full: "ઑ", matra: "ૉ" },
  },
};

export const PHONETIC_SEQUENCES: Record<string, Record<string, string>> = {
  Hindi: {
    "क+h": "ख",
    "ग+h": "घ",
    "च+h": "छ",
    "ज+h": "झ",
    "ट+h": "ठ",
    "ड+h": "ढ",
    "त+h": "थ",
    "द+h": "ध",
    "प+h": "फ",
    "ब+h": "भ",
    "स+h": "श",
    "श+h": "ष",
    "स+w": "स्व",
    "श+w": "श्व",
    "क+s": "क्ष",
    "क्ष+h": "क्ष",
    "ज+n": "ज्ञ",
    "ग+n": "ज्ञ",
    "ड+d": "ड्ड",
    "न+n": "न्न",
    H: "ः",
    M: "ं",
    ".": "़",
    "र+r": "र्",
    "क+r": "क्र",
    "प+r": "प्र",
    "द+r": "द्र",
    "त+r": "त्र",
    "ग+r": "ग्र",
    "भ+r": "भ्र",
    "श+r": "श्र",
    "स+r": "स्र",
    "ज+r": "ज्र",
    "ध+r": "ध्र",
    "ख+r": "ख्र",
    "ट+r": "ट्र",
    "ड+r": "ड्र",
    "ं+m": "ँ",
    "म+m": "ँ",
  },
  Gujarati: {
    "ક+h": "ખ",
    "ગ+h": "ઘ",
    "ચ+h": "છ",
    "જ+h": "ઝ",
    "ટ+h": "ઠ",
    "ડ+h": "ઢ",
    "ત+h": "થ",
    "દ+h": "ધ",
    "પ+h": "ફ",
    "બ+h": "ભ",
    "સ+h": "શ",
    "શ+h": "ષ",
    "ષ+h": "ષ",
    "ન+g": "ઙ",
    "ન+y": "ઞ",
    "સ+w": "સ્વ",
    "શ+w": "શ્વ",
    "ક+s": "ક્ષ",
    "ક્ષ+h": "ક્ષ",
    "જ+n": "જ્ઞ",
    "ગ+n": "જ્ઞ",
    "ટ+t": "ટ્ટ",
    "ડ+d": "ડ્ડ",
    "ન+n": "ન્ન",
    "ત+ત": "ત્ત",
    "દ+દ": "દ્ધ",
    "ષ+ટ": "ષ્ટ",
    H: "ઃ",
    M: "ં",
    ".": "઼",
    "'": "ઽ",
    "ર+r": "ર્",
    "ક+r": "ક્ર",
    "પ+r": "પ્ર",
    "દ+r": "દ્ર",
    "ત+r": "ત્ર",
    "ગ+r": "ગ્ર",
    "ભ+r": "ભ્ર",
    "શ+r": "શ્ર",
    "સ+r": "સ્ર",
    "જ+r": "જ્ર",
    "ધ+r": "ધ્ર",
    "ખ+r": "ખ્ર",
    "ટ+r": "ટ્ર",
    "ડ+r": "ડ્ર",
    "ં+m": "ઁ",
    "મ+m": "ઁ",
  },
};

export const RELATED_LETTERS: Record<string, Record<string, string[]>> = {
  Hindi: {
    त: ["ट"],
    ट: ["त"],
    द: ["ड"],
    ड: ["द"],
    न: ["ण"],
    ण: ["न"],
    ल: ["ळ"],
    ळ: ["ल"],
    स: ["श", "ष"],
    श: ["स", "ष"],
    ष: ["स", "श"],
    क: ["ख"],
    ख: ["क"],
    ग: ["घ"],
    घ: ["ग"],
    च: ["छ"],
    छ: ["च"],
    ज: ["झ"],
    झ: ["ज"],
    प: ["फ"],
    फ: ["प"],
    ब: ["भ"],
    भ: ["ब"],
    "ं": ["ँ"],
    "ँ": ["ं"],
  },
  Gujarati: {
    ત: ["ટ"],
    ટ: ["ત"],
    દ: ["ડ"],
    ડ: ["દ"],
    ન: ["ણ"],
    ણ: ["ન"],
    લ: ["ળ"],
    ળ: ["લ"],
    સ: ["શ", "ષ"],
    શ: ["સ", "ષ"],
    ષ: ["સ", "શ"],
    ક: ["ખ"],
    ખ: ["ક"],
    ગ: ["ઘ"],
    ચ: ["છ"],
    છ: ["ચ"],
    જ: ["ઝ"],
    ઝ: ["જ"],
    પ: ["ફ"],
    ફ: ["પ"],
    બ: ["ભ"],
    ભ: ["બ"],
    "ં": ["ઁ"],
    "ઁ": ["ં"],
  },
};

export function getRelatedLetters(
  char: string,
  script: "Hindi" | "Gujarati",
): string[] {
  return RELATED_LETTERS[script][char] || [];
}

export function isIndicConsonant(char: string): boolean {
  if (!char) return false;
  // Get the base character (handle strings like 'ક્ષ')
  const baseChar = char.charAt(0);
  const code = baseChar.charCodeAt(0);

  // Hindi/Devanagari Consonants (including extensions)
  if (code >= 0x0915 && code <= 0x0939) return true;
  if (code >= 0x0958 && code <= 0x095f) return true; // Refined Hindi (ka with nukta etc)
  if (code === 0x0934) return true; // LLA
  if (code === 0x093c) return true; // Devanagari Nuqta

  // Gujarati Consonants
  if (code >= 0x0a95 && code <= 0x0ab9) return true;
  if (code === 0x0af9) return true; // ZHA (rare)
  if (code === 0x0abc) return true; // Gujarati Nuqta

  return false;
}

export function isIndicFullVowel(char: string): boolean {
  if (!char) return false;
  const code = char.charCodeAt(0);
  if (code >= 0x0904 && code <= 0x0914) return true; // Hindi
  if (code >= 0x0a85 && code <= 0x0a94) return true; // Gujarati
  return false;
}

export function getHalant(language: LanguageType): string {
  if (language.includes("Hindi")) return "\u094d";
  if (language.includes("Gujarati")) return "\u0acd";
  return "";
}

export function getMappedCharacter(
  language: LanguageType,
  code: string,
  shift: boolean,
  altGr: boolean = false,
): string | null {
  const config = LANGUAGE_CONFIGS[language];
  if (!config || language === "English") return null;

  const charMapping = config.mapping[code];
  if (!charMapping) return null;

  if (altGr && charMapping.altGr) return charMapping.altGr;
  return shift ? charMapping.shift : charMapping.default;
}
// --- Google-Standard Phonetic Engine ---

export interface PhoneticState {
  type: "consonant" | "vowel" | "other" | "none";
  char: string;
}

/**
 * PhoneticEngine - A professional-grade transliteration engine.
 * Decouples linguistic transformation logic from the UI layer.
 * Follows ITRANS standards for Indic scripts.
 */
export class PhoneticEngine {
  private script: "Hindi" | "Gujarati";
  private language: LanguageType;

  constructor(language: LanguageType) {
    this.language = language;
    this.script = language.includes("Hindi") ? "Hindi" : "Gujarati";
  }

  /**
   * Processes a single keystroke and returns the transformation action.
   */
  /**
   * Processes a single keystroke and returns the transformation action.
   */
  public getAction(
    rawKey: string,
    keyCode: string,
    prevChar: string,
    state: PhoneticState | null,
  ): {
    insertchar: string;
    replaceCount: number;
    newState: PhoneticState;
  } | null {
    const key = rawKey.toLowerCase();
    const isShift = rawKey !== rawKey.toLowerCase();

    // 1. Resolve Explicit Halant (Virama) - Key 'q'
    if (key === "q" || key === "્" || key === "્") {
      return {
        insertchar: getHalant(this.language),
        replaceCount: 0,
        newState: { type: "other", char: "q" },
      };
    }

    // 1.5. Resolve Special 'c' Sequences (c -> Ka, ch -> Cha, chh -> Chha)
    if (!isShift) {
      const isGujarati = this.script === "Gujarati";
      const ka = isGujarati ? "ક" : "क";
      const cha = isGujarati ? "ચ" : "च";
      const chha = isGujarati ? "છ" : "छ";

      if (key === "c" && (keyCode === "KeyC" || rawKey === "c")) {
        return {
          insertchar: ka,
          replaceCount: 0,
          newState: { type: "consonant", char: "c" },
        };
      }
      if (state?.char === "c" && key === "h") {
        return {
          insertchar: cha,
          replaceCount: 1,
          newState: { type: "consonant", char: "ch" },
        };
      }
      if (state?.char === "ch" && key === "h") {
        return {
          insertchar: chha,
          replaceCount: 1,
          newState: { type: "consonant", char: "chh" },
        };
      }
    }

    // 2. Resolve Multi-Vowel Sequences (aa, ai, au, ee, oo)
    const vowelSeq = this.getVowelSequence(key, state);
    if (vowelSeq) {
      const isAfterConsonant = isIndicConsonant(prevChar) || prevChar === "ઽ";
      const charToInsert = isAfterConsonant ? vowelSeq.matra : vowelSeq.full;

      let replaceCount = 1;
      // If we are transforming 'a' -> 'aa' (inherent completion), and 'a' was hidden,
      // we only replace if 'a' actually inserted a character.
      if (state?.type === "vowel" && state.char === "a" && isAfterConsonant) {
        replaceCount = 0;
      }

      return {
        insertchar: charToInsert,
        replaceCount: replaceCount,
        newState: { type: "vowel", char: state!.char + key },
      };
    }

    // 3. Resolve Vowel Forms (Full vs Matra) with Inherent Vowel logic
    const vowelMatch =
      VOWEL_FORMS[this.script][rawKey] || VOWEL_FORMS[this.script][key];
    if (vowelMatch) {
      const isAfterConsonant = isIndicConsonant(prevChar) || prevChar === "ઽ";

      // Google Logic for 'a':
      // - First 'a' after a consonant completes the character (inherent, no insertion).
      // - Subsequent 'a' (already handled in sequences) or context determines matra.
      if (key === "a" && isAfterConsonant) {
        // If we just typed a consonant, the first 'a' is "absorbed"
        if (state?.type === "consonant") {
          return {
            insertchar: "",
            replaceCount: 0,
            newState: { type: "vowel", char: "a" },
          };
        }
      }

      return {
        insertchar: isAfterConsonant ? vowelMatch.matra : vowelMatch.full,
        replaceCount: 0,
        newState: { type: "vowel", char: rawKey },
      };
    }

    // 4. Resolve Special Markers (M, H, ~, .)
    const markerMatch = PHONETIC_SEQUENCES[this.script][rawKey];
    if (markerMatch && ["H", "M", "~", ".", "'"].includes(rawKey)) {
      return {
        insertchar: markerMatch,
        replaceCount: 0,
        newState: { type: "other", char: key },
      };
    }

    // 5. Resolve Phonetic Sequences (kh, gh, sh, ksh, etc.)
    const sequenceMatch = PHONETIC_SEQUENCES[this.script][prevChar + "+" + key];
    if (sequenceMatch && state?.type !== "vowel") {
      return {
        insertchar: sequenceMatch,
        replaceCount: 1,
        newState: {
          type: isIndicConsonant(sequenceMatch) ? "consonant" : "other",
          char: key,
        },
      };
    }

    // 6. Natural Consonant Mapping (Full by Default)
    // In Google Standard, consonants are full. mn -> મન, not મ્ન.
    const mappedChar = getMappedCharacter(this.language, keyCode, isShift);
    if (mappedChar && isIndicConsonant(mappedChar)) {
      // --- Smart Reph Reordering ---
      // If user types a Reph character (Shift+R) immediately AFTER a consonant,
      // we reorder it to be logically BEFORE the consonant so it appears at the TOP.
      // This makes typing "reph as a matra" work intuitively.
      const isRephChar = mappedChar === "ર્" || mappedChar === "र्";
      if (isRephChar && isIndicConsonant(prevChar)) {
        return {
          insertchar: mappedChar + prevChar,
          replaceCount: 1, // Remove the previous consonant to re-insert it with Reph
          newState: { type: "consonant", char: key },
        };
      }

      return {
        insertchar: mappedChar,
        replaceCount: 0,
        newState: { type: "consonant", char: key },
      };
    }

    return null;
  }

  private getVowelSequence(
    key: string,
    state: PhoneticState | null,
  ): { full: string; matra: string } | null {
    if (!state || state.type !== "vowel") return null;
    const combined = state.char + key;
    return VOWEL_FORMS[this.script][combined] || null;
  }
}
